'use client';
import { JSX, useEffect, useState } from 'react';
import classNames from 'classnames';

// components
import { AtArrow } from '../at-arrow';

import { EIcons } from '../at-icon/icons';
import { AtIcon } from '../at-icon';
import {
  EClickableSize,
  EClickableVariants,
} from '../at-clickable/at-clickable.types';
import { AtClickable } from '../at-clickable';

// swiper
import 'swiper/css';
import { Swiper } from 'swiper/react';
import { A11y } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper/types';

// styles
import { carouselVariants } from './at-carousel.variants';
import { IAtcarouselProps } from './at-carousel.types';
import { AtDivider } from '../at-divider';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '../at-divider/at-divider.types';

/**
 * `AtCarousel` is a flexible wrapper around Swiper that supports multiple button layouts,
 * autoplay control, A11y enhancements, and design-specific intents.
 *
 * - Accepts all native Swiper props (excluding `children`)
 * - Expects pre-mapped `<SwiperSlide>` children
 * - Use `buttonPosition` to control layout of navigation buttons
 * - Use `autoplayControl` in `hero` layouts to toggle pause/play buttons
 *
 * Accessibility:
 * - Adds `aria-hidden` and `tabindex` logic for better screen reader behavior
 * - Enables keyboard navigation with left/right arrows
 *
 * Known caveat:
 * - When `slideToVal === 0`, we check `isInitialInteraction` to avoid Swiper’s internal bug
 */

export const AtCarousel = ({
  ariaLabel = 'carousel',
  autoplayControl,
  buttonPosition = 'below',
  buttonTheme = 'light',
  children,
  containerClasses,
  contentClasses,
  controlClasses,
  disableNav = false,
  intent = 'default',
  modules = [],
  loop = true,
  showDivider = false,
  swiperRef,
  swiperClasses,
  slideToVal,
  isInitialInteraction,
  onSwiper,
  ...SwiperProps
}: IAtcarouselProps): JSX.Element => {
  const [swiper, setSwiper] = useState<SwiperType | null>(null);

  useEffect(() => {
    if (!swiper) return;
    const updateSlideAccessibility = () => {
      swiper?.slides?.forEach((slide, index) => {
        const isActive = index === swiper.activeIndex;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        // Ensure non-interactive text is focusable
        slide.querySelectorAll('p, span, div').forEach((el) => {
          if (!el.querySelector('a, button, input, select, textarea')) {
            el.setAttribute('tabindex', isActive ? '0' : '-1');
          }
        });

        // Disable focusable elements in inactive slides
        slide
          .querySelectorAll('a, button, input, select, textarea')
          .forEach((el) => {
            el.setAttribute('tabindex', isActive ? '0' : '-1');
          });
      });
    };

    // use arrows instead of just tabbing to nav buttons
    const handleKeydown = (event: KeyboardEvent) => {
      if (!swiper || document.activeElement !== swiper.el) return;

      if (event.key === 'ArrowRight') {
        swiper.slideNext();
      } else if (event.key === 'ArrowLeft') {
        swiper.slidePrev();
      }
    };

    const carouselElement = swiper.el;
    if (carouselElement) {
      carouselElement.addEventListener('keydown', handleKeydown);
    }

    updateSlideAccessibility();
    swiper.on('slideChange', updateSlideAccessibility);

    return () => {
      swiper.off('slideChange', updateSlideAccessibility);

      if (carouselElement) {
        carouselElement.removeEventListener('keydown', handleKeydown);
      }
    };
  }, [swiper]);

  const handlePrevClick = () => {
    if (!swiper) return;
    swiperRef?.current?.autoplay?.stop();
    swiper.slidePrev();
  };

  const handleNextClick = () => {
    if (!swiper) return;
    swiperRef?.current?.autoplay?.stop();
    swiper.slideNext();
  };

  useEffect(() => {
    if (!swiper || typeof slideToVal !== 'number') return;

    swiper.update();

    const isFirstSlide = slideToVal === 0;

    if (loop) {
      // Handle Swiper quirk when targeting index 0 in loop mode
      if (isFirstSlide && !isInitialInteraction) {
        swiper.slideToLoop(0, 0); // 0 duration for immediate jump
      } else {
        swiper.slideToLoop(slideToVal, 0);
      }
    } else {
      // Non-looping case
      if (isFirstSlide && !isInitialInteraction) {
        swiper.slideTo(0, 0);
      } else {
        swiper.slideTo(slideToVal, 0);
      }
    }
  }, [swiper, slideToVal, loop, isInitialInteraction]);

  const renderLeftButton = () => (
    <div className="absolute bottom-[calc(50%_-_1.25rem)] left-[1.375rem] z-10">
      <AtArrow
        aria-controls="carousel"
        direction="prev"
        isDisabled={disableNav}
        theme={buttonTheme}
        onClick={handlePrevClick}
      />
    </div>
  );

  const renderRightButton = () => (
    <div className="absolute right-[1.375rem] top-[calc(50%_-_1.25rem)] z-10">
      <AtArrow
        aria-controls="carousel"
        direction="next"
        isDisabled={disableNav}
        theme={buttonTheme}
        onClick={handleNextClick}
      />
    </div>
  );

  const renderButtons = () => (
    <div className="flex items-center gap-2">
      <AtArrow
        aria-controls="carousel"
        direction="prev"
        isDisabled={disableNav}
        theme={buttonTheme}
        onClick={handlePrevClick}
      />
      <AtArrow
        aria-controls="carousel"
        direction="next"
        isDisabled={disableNav}
        theme={buttonTheme}
        onClick={handleNextClick}
      />
    </div>
  );

  return (
    <>
      <div className={containerClasses} data-testid="carousel">
        <div
          className={classNames(
            [contentClasses, carouselVariants({ intent })],
            'relative',
          )}
        >
          {buttonPosition === 'side' && <>{renderLeftButton()}</>}
          <Swiper
            {...SwiperProps}
            a11y={{
              enabled: true,
            }}
            aria-label={ariaLabel}
            aria-roledescription="carousel"
            className={`${swiperClasses}`}
            keyboard={{ enabled: true, onlyInViewport: false }}
            loop={loop}
            modules={[A11y, ...modules]}
            role="group"
            aria-live="assertive"
            tabIndex={0}
            onSwiper={(swiper) => {
              setSwiper(swiper);
              if (typeof onSwiper === 'function') onSwiper(swiper); // external ref
            }}
          >
            {children}
          </Swiper>
          {buttonPosition === 'side' && <>{renderRightButton()}</>}
          {showDivider && (
            <AtDivider
              decorative={true}
              color={EDividerColors.BLACK}
              direction={EDividerDirectionalVariants.VERTICAL}
              intent={EDividerVariants.THIN}
              customClasses="w-0.5 h-160"
            />
          )}
        </div>

        {buttonPosition === 'end' && (
          <div className={classNames('ml-5 flex', controlClasses)}>
            {renderButtons()}
          </div>
        )}
      </div>

      {buttonPosition === 'below' && (
        <div
          className={classNames(
            'flex w-full justify-center lg:justify-end',
            controlClasses,
          )}
        >
          {renderButtons()}
        </div>
      )}

      {/* this keeps the buttons aligned under the first slide until we reach the mobile breakpoint -- then they're centered  */}
      {buttonPosition === 'hero' && (
        <div
          className={classNames(
            'flex items-center justify-center gap-2 md:pr-6 lg:pr-13 xl:pr-20 2xl:pr-24 3xl:pr-0',
            SwiperProps.autoplay ? 'md:justify-between' : 'md:justify-end',
            controlClasses,
          )}
        >
          {SwiperProps.autoplay && (
            <AtClickable
              ariaLabel={
                autoplayControl?.isRunning ? 'Pause carousel' : 'Play carousel'
              }
              iconPosition="left"
              size={EClickableSize.SM}
              variant={EClickableVariants.PRIMARY}
              onClick={autoplayControl?.onToggle}
              interactLocation="or-carousel-hero"
            >
              <AtIcon
                color="var(--color-button-text-primary)"
                icon={autoplayControl?.isRunning ? EIcons.PAUSE : EIcons.PLAY}
                className="text-sm"
              />
            </AtClickable>
          )}

          <div>{renderButtons()}</div>
        </div>
      )}
    </>
  );
};
